import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DragSource, DropTarget } from 'react-dnd'
import flow from 'lodash.flow';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import { makeGetTask } from '../selectors/tasks';
import { makeGetTaskNotifications } from '../selectors/notifications';

import { updateTask, assignTask, unassignTask } from '../actions/tasks';

import { isTempId } from '../utils/helpers';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import TaskListItem from 'components/TaskListItem';
import TaskActions from 'components/TaskActions';

import Placeholder from './placeholders/subtask-item.placeholder';

const subtaskSource = {
    beginDrag(props) {
        setTimeout(props.onDragStart && props.onDragStart);

        return {
            id: props.id,
            index: props.index
        }
    },

    endDrag(props) {
        props.onDragEnd && props.onDragEnd(props.id);
    },
    canDrag(props) {
        if (props.subtasksInEditMode.includes(props.id)) {
            return false
        }

        return true;
    }
}

const subtaskTarget = {
    drop(props, monitor, component) {
        props.moveSubtask && props.moveSubtask(monitor.getItem().id, props.id);
    },

    hover(props, monitor, component) {
        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.index;

        if (dragIndex === hoverIndex) {
            return;
        }

        props.moveSubtask(monitor.getItem().id, props.id, true)

        monitor.getItem().index = hoverIndex
    }
}

class SubtaskHolder extends Component {
    static contextType = RolesAndPermissionsContext;

    static propTypes = {
        id: PropTypes.string,
        index: PropTypes.number,
        isDragging: PropTypes.bool,
        isOver: PropTypes.bool,
        subtask: PropTypes.any,
        withControls: PropTypes.bool,
        editable: PropTypes.bool,
        onConvertToTask: PropTypes.func,
        onMoveToTask: PropTypes.func,
        onComplete: PropTypes.func,
        onUncomplete: PropTypes.func,
        onNameChange: PropTypes.func,
        onEnterPress: PropTypes.func,
        onDragStart: PropTypes.func,
        onDragEnd: PropTypes.func,
        moveSubtask: PropTypes.func,
        connectDragPreview: PropTypes.func,
        connectDragSource: PropTypes.func,
        connectDropTarget: PropTypes.func,
        provideController: PropTypes.func
    };

    static defaultProps = {
        withControls: true,
        editable: true
    }

    constructor(props) {
        super(props);

        this.state = {
            name: props.subtask && props.subtask.name,
            editing: false
        };
    }

    handleComplete = () => {
        if (!this.context?.canUser('editSubtask')) {
            return;
        }

        if (this.state.name === '') {
            this.beginEdit();

            return;
        }

        return this.props.onComplete?.(this.props.id);
    }

    handleUncomplete = () => {
        if (!this.context?.canUser('editSubtask')) {
            return;
        }

        return this.props.onUncomplete?.(this.props.id);
    }

    handleSubtaskClick = id => {
        if (isTempId(id)) {
            return;
        }

        if (this.props.onCommentsClick) {
            if (this.clickTimeout) {
                clearTimeout(this.clickTimeout);
            }

            this.clickTimeout = setTimeout(() => {
                this.props.onCommentsClick(id);
            }, 200);
        } else {
            this.beginEdit();
        }
    }

    handleSubtaskDoubleClick = () => {
        if (this.clickTimeout) {
            clearTimeout(this.clickTimeout);
        }

        this.beginEdit();
    }

    toggleEdit() {
        if (this.state.editing) {
            this.endEdit();
        } else {
            this.beginEdit();
        }
    }

    beginEdit() {
        this.subtaskCtrl && this.subtaskCtrl.beginEdit();
    }

    endEdit() {
        this.subtaskCtrl && this.subtaskCtrl.endEdit();
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.props.id !== nextProps.id) return true;
        if (this.props.isDragging !== nextProps.isDragging) return true;
        if (this.props.isOver !== nextProps.isOver) return true;
        if (this.props.subtask !== nextProps.subtask) return true;
        if (this.props.subtasksInEditMode !== nextProps.subtasksInEditMode) return true;
        if (this.props.hasNotifications !== nextProps.hasNotifications) return true;
        if (this.state.editing !== nextState.editing) return true;
        if (this.state.name !== nextState.name) return true;

        return false;
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            beginEdit: () => {
                return this.beginEdit();
            },
            hasFocus: () => {
                return this.subtaskCtrl && this.subtaskCtrl.hasFocus();
            }
        });
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    render() {
        const { subtask, t } = this.props;

        if ((!subtask && !this.props.new) || (subtask && subtask.loading)) {
            return <Placeholder />;
        }

        const temp = isTempId(subtask?.id);
        const withControls = this.props.withControls && subtask?.id && !temp && !this.props.new && !this.props.isPublic;

        const element = <TaskListItem
            id={subtask?.id}
            new={this.props.new}
            isSubtask={true}
            richPreview={this.props.richPreview}
            name={subtask?.name}
            completed={subtask?.completed}
            completedBy={subtask?.completed && subtask.completed_by}
            completedOn={subtask?.completed && subtask.completed_on}
            comments={subtask?.comments}
            labels={subtask?.labels}
            dueDate={subtask?.due_date}
            description={subtask?.description}
            assignees={subtask && !this.props.isPublic && subtask.assignees}
            namePlaceholder={ t('Add a new subtask') }
            isDragging={this.props.isDragging}
            isOver={this.props.isOver}
            projectId={this.props.projectId}
            hasNotifications={this.props.hasNotifications}
            selectListDisabled={true}
            canEdit={this.props.editable && this.context?.canUser('editSubtask')}
            onClick={this.handleSubtaskClick}
            onDoubleClick={this.handleSubtaskDoubleClick}
            onUncomplete={this.handleUncomplete}
            onComplete={this.handleComplete}
            onEditStart={this.props.onEditStart}
            onEditEnd={this.props.onEditEnd}
            onNameChange={this.props.onNameChange}
            onEnterPress={this.props.onEnterPress}
            onCommentsClick={withControls && this.props.onCommentsClick}
            onNamePaste={this.props.onNamePaste}
            provideController={ctrl => this.subtaskCtrl = ctrl}
            utilElement={withControls && <TaskActions
                id={subtask?.id}
                className="item-actions"
                innerClassName="btn btn-square btn-xxxsmall btn-simple btn-icon item-click-action"
            />}
            showAssignees={!this.props.isPublic && !this.props.new && this.props.projectId}
        />

        if (this.props.moveSubtask && this.context?.canUser('editSubtask') && this.props.onDragEnd && !this.props.subtasksInEditMode.includes(this.props.id)) {
            return this.props.connectDragSource(this.props.connectDropTarget(<div className="subtask-holder">{element}</div>))
        }

        return <div className="subtask-holder">{element}</div>;
    }
}


const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getTaskNotifications = makeGetTaskNotifications();

    return ({ tasks, notifications }, ownProps) => ({
        subtask: (ownProps.id && getTask(tasks.items, ownProps.id)) || ownProps.subtask,
        hasNotifications: ownProps.id && Boolean(getTaskNotifications(notifications.items, ownProps.id, true).length) || false
    })
}

export default connect(makeMapStateToProps)(flow(
    DragSource('subtask', subtaskSource, (connect, monitor) => ({
        connectDragSource: connect.dragSource(),
        isDragging: monitor.isDragging()
    })),
    DropTarget('subtask', subtaskTarget, (connect, monitor) => ({
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver()
    })),
)(withTranslation(['task', 'user'])(SubtaskHolder)));
